import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

export const proxyImg = (u) => {
  if (!u) return u;
  if (u.includes("/api/files/")) return u; // own uploaded assets are same-origin
  return `${API}/img?u=${encodeURIComponent(u)}`;
};

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("klyroo_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const api = {
  // public
  home: () => client.get("/home").then((r) => r.data),
  tools: (params) => client.get("/tools", { params }).then((r) => r.data),
  toolCategories: () => client.get("/tools/categories").then((r) => r.data),
  tool: (slug) => client.get(`/tools/${slug}`).then((r) => r.data),
  models: (params) => client.get("/models", { params }).then((r) => r.data),
  model: (slug) => client.get(`/models/${slug}`).then((r) => r.data),
  comparisons: (params) => client.get("/comparisons", { params }).then((r) => r.data),
  comparison: (slug) => client.get(`/comparisons/${slug}`).then((r) => r.data),
  articles: (params) => client.get("/articles", { params }).then((r) => r.data),
  article: (slug) => client.get(`/articles/${slug}`).then((r) => r.data),
  ads: (placement) => client.get("/ads", { params: { placement } }).then((r) => r.data),
  adImpression: (slug) => client.post(`/ads/${slug}/impression`).then((r) => r.data),
  adClick: (slug) => client.post(`/ads/${slug}/click`).then((r) => r.data),
  deals: (params) => client.get("/deals", { params }).then((r) => r.data),
  deal: (slug) => client.get(`/deals/${slug}`).then((r) => r.data),
  search: (q) => client.get("/search", { params: { q } }).then((r) => r.data),
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  // admin
  adminStats: () => client.get("/admin/stats").then((r) => r.data),
  adminList: (coll) => client.get(`/admin/${coll}`).then((r) => r.data),
  adminUpsert: (coll, data) => client.post(`/admin/${coll}`, { data }, { timeout: 45000 }).then((r) => r.data),
  adminDelete: (coll, slug) => client.delete(`/admin/${coll}/${slug}`).then((r) => r.data),
  ingestDeals: (data) => client.post("/admin/deals/ingest", { data }, { timeout: 120000 }).then((r) => r.data),
  submitSuggestion: (data) => client.post("/suggestions", data).then((r) => r.data),
  adminSuggestions: (params) => client.get("/admin/suggestions", { params }).then((r) => r.data),
  updateSuggestion: (id, data) => client.patch(`/admin/suggestions/${id}`, { data }).then((r) => r.data),
  submitAdvertising: (data) => client.post("/advertising-requests", data).then((r) => r.data),
  adminAdvertising: (params) => client.get("/admin/advertising-requests", { params }).then((r) => r.data),
  updateAdvertising: (id, data) => client.patch(`/admin/advertising-requests/${id}`, { data }).then((r) => r.data),
  analyticsHealth: () => client.get("/admin/analytics/health").then((r) => r.data),
  // Uploads are safe to retry (each attempt stores a fresh UUID path — no duplicates),
  // so we auto-retry transient origin/proxy hiccups (Cloudflare 5xx / dropped connection).
  adminUpload: async (fileForm, folder = "misc") => {
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await client.post(`/admin/upload?folder=${folder}`, fileForm,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        return r.data;
      } catch (e) {
        lastErr = e;
        const st = e.response?.status;
        const transient = !e.response || (st >= 500 && st < 600);
        if (!transient) break;
        if (attempt < 2) await new Promise((res) => setTimeout(res, 800 * (attempt + 1)));
      }
    }
    throw lastErr;
  },
  adminGetHomepage: () => client.get("/admin/homepage").then((r) => r.data),
  adminSaveHomepage: (data) => client.put("/admin/homepage", { data }).then((r) => r.data),
  llmProviders: () => client.get("/admin/llm/providers").then((r) => r.data),
  llmCatalog: () => client.get("/admin/llm/catalog").then((r) => r.data),
  llmSaveProvider: (pid, data) => client.put(`/admin/llm/providers/${pid}`, { data }).then((r) => r.data),
  llmTestProvider: (pid) => client.post(`/admin/llm/providers/${pid}/test`).then((r) => r.data),
  llmRunTask: (data) => client.post("/admin/llm/run", { data }).then((r) => r.data),
  llmRoutes: () => client.get("/admin/llm/routes").then((r) => r.data),
  llmRouteTask: (data) => client.post("/admin/llm/route", { data }).then((r) => r.data),
  researchSources: () => client.get("/admin/research/sources").then((r) => r.data),
  researchSaveSource: (data) => client.post("/admin/research/sources", { data }).then((r) => r.data),
  researchTestSource: (data) => client.post("/admin/research/sources/test", { data }).then((r) => r.data),
  researchSeedSources: () => client.post("/admin/research/sources/seed", {}).then((r) => r.data),
  researchDeleteSource: (id) => client.delete(`/admin/research/sources/${id}`).then((r) => r.data),
  researchItems: () => client.get("/admin/research/items").then((r) => r.data),
  researchItemDetail: (id) => client.get(`/admin/research/items/${id}`).then((r) => r.data),
  researchAssess: (id, data) => client.post(`/admin/research/items/${id}/assess`, { data }).then((r) => r.data),
  researchAssessStory: (id, data) => client.post(`/admin/research/items/${id}/assess-story`, { data }).then((r) => r.data),
  researchBrief: (id, data) => client.post(`/admin/research/items/${id}/research-brief`, { data }).then((r) => r.data),
  researchUsage: (period) => client.get("/admin/research/usage", { params: { period } }).then((r) => r.data),
  researchGenerateDraft: (id, data) => client.post(`/admin/research/items/${id}/generate-draft`, { data }).then((r) => r.data),
  researchApplyDraft: (id, data) => client.post(`/admin/research/items/${id}/apply-draft`, { data }).then((r) => r.data),
  researchArticleContext: (id) => client.get(`/admin/research/article-context/${id}`).then((r) => r.data),
  researchContentReadiness: (id, data) => client.post(`/admin/research/items/${id}/content-readiness`, { data }).then((r) => r.data),
  researchEnrich: (id, data) => client.post(`/admin/research/items/${id}/enrich`, { data }).then((r) => r.data),
  articleQualityCheck: (slug, data) => client.post(`/admin/articles/${slug}/quality-check`, { data }).then((r) => r.data),
  regenerateDepth: (slug, data) => client.post(`/admin/articles/${slug}/regenerate-depth`, { data }).then((r) => r.data),
  articlePublish: (slug, data) => client.post(`/admin/articles/${slug}/publish`, { data }).then((r) => r.data),
  articleUnpublish: (slug) => client.post(`/admin/articles/${slug}/unpublish`, {}).then((r) => r.data),
  adminGetOne: (coll, slug) => client.get(`/admin/${coll}`).then((r) => (r.data.items || []).find((x) => x.slug === slug)),
  imageCatalog: () => client.get("/admin/image/catalog").then((r) => r.data),
  imageProviders: () => client.get("/admin/image/providers").then((r) => r.data),
  imageSaveProvider: (pid, data) => client.put(`/admin/image/providers/${pid}`, { data }).then((r) => r.data),
  imageTestProvider: (pid) => client.post(`/admin/image/providers/${pid}/test`, {}).then((r) => r.data),
  imagePrompt: (data) => client.post("/admin/image/prompt", { data }).then((r) => r.data),
  imageGenerate: (data) => client.post("/admin/image/generate", { data }).then((r) => r.data),
  imageApply: (data) => client.post("/admin/image/apply", { data }).then((r) => r.data),
  researchAssessmentReview: (id, data) => client.patch(`/admin/research/items/${id}/assessment-review`, { data }).then((r) => r.data),
  researchUpdateItem: (id, data) => client.patch(`/admin/research/items/${id}`, { data }).then((r) => r.data),
  researchBulk: (data) => client.post("/admin/research/items/bulk", { data }).then((r) => r.data),
  researchDeleteItem: (id) => client.delete(`/admin/research/items/${id}`).then((r) => r.data),
  researchFetch: () => client.post("/admin/research/fetch", {}).then((r) => r.data),
  researchConfig: () => client.get("/admin/research/config").then((r) => r.data),
  opportunities: () => client.get("/admin/research/opportunities").then((r) => r.data),
  opportunitySave: (data) => client.post("/admin/research/opportunities", { data }).then((r) => r.data),
  opportunityStatus: (id, data) => client.patch(`/admin/research/opportunities/${id}`, { data }).then((r) => r.data),
  opportunityDelete: (id) => client.delete(`/admin/research/opportunities/${id}`).then((r) => r.data),
};

export default client;
